import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Milestone, Property } from '../api/detailsContracts';
import { detailsService } from '../services/detailsService';
import type { ModelKind } from '@/features/projects/utils/modelSections';
import { modelKindOf } from '../utils/modelKind';
import { useCapabilities } from './useCapabilities';
import { workspaceKeys } from './workspaceKeys';

const keys = {
  properties: (projectId: string, modelId?: number) =>
    ['workspace', 'properties', projectId, modelId ?? 'project'] as const,
  milestones: (projectId: string) => ['workspace', 'milestones', projectId] as const,
  modelMembers: (projectId: string, modelId: number) => ['workspace', 'model-members', projectId, modelId] as const,
};

/** Key-value properties of a project or one of its models. Off while Core does not serve them. */
export function useProperties(projectId: string, modelId?: number, enabled = true) {
  const { capabilities } = useCapabilities();
  return useQuery({
    queryKey: keys.properties(projectId, modelId),
    queryFn: () => detailsService.properties(projectId, modelId),
    enabled: capabilities.projectDetails && enabled,
  });
}

/** Whether a model is BIM or CAD; BIM outside a project, while loading, or when Core has no properties. */
export function useModelKind(projectId: string | null, modelId: number): ModelKind {
  const query = useProperties(projectId ?? '', modelId, projectId !== null);
  return modelKindOf(query.data?.items);
}

export function useSaveProperties(projectId: string, modelId?: number) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ items, etag }: { items: readonly Property[]; etag: string }) =>
      detailsService.saveProperties(projectId, modelId, items, etag),
    onSuccess: (saved) => {
      cache.setQueryData(keys.properties(projectId, modelId), saved);
    },
  });
}

export function useMilestones(projectId: string) {
  const { capabilities } = useCapabilities();
  return useQuery({
    queryKey: keys.milestones(projectId),
    queryFn: () => detailsService.milestones(projectId),
    enabled: capabilities.projectDetails,
  });
}

export function useSaveMilestones(projectId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ items, etag }: { items: readonly Milestone[]; etag: string }) =>
      detailsService.saveMilestones(projectId, items, etag),
    onSuccess: (saved) => {
      cache.setQueryData(keys.milestones(projectId), saved);
    },
  });
}

/** The project members assigned to one of its models. Off while Core does not serve them. */
export function useModelMembers(projectId: string, modelId: number, enabled = true) {
  const { capabilities } = useCapabilities();
  return useQuery({
    queryKey: keys.modelMembers(projectId, modelId),
    queryFn: () => detailsService.modelMembers(projectId, modelId),
    enabled: capabilities.projectDetails && enabled,
  });
}

export function useSaveModelMembers(projectId: string, modelId: number) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: ({ subjects, etag }: { subjects: readonly string[]; etag: string }) =>
      detailsService.saveModelMembers(projectId, modelId, subjects, etag),
    onSuccess: (saved) => {
      cache.setQueryData(keys.modelMembers(projectId, modelId), saved);
      // A member who is no longer assigned stops being the responsible one, so the project is read again.
      void cache.invalidateQueries({ queryKey: workspaceKeys.project(projectId) });
    },
  });
}
